import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Form } from 'antd'

import { routes } from 'router'
import { VALIDATION_MESSAGES } from 'constants/txt'
import { notification } from 'components/Notification'
import { ROLES } from 'features/Permission'
import {
  convertDetailsToForm,
  convertFormToDetails,
  convertFormToManualCreation,
  disabledDateSameOrBefore,
  disabledDateFinishDt
} from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/utils'
import { useContractDetailsContext } from 'features/ContractDetails/Providers/ContractDetailsContextProvider'
import { IContractDetailsForm } from 'features/ContractDetails/ContractDetailsGeneral/ContractDetailsForm/types'
import {
  CONTRACT_STATUS,
  SHIPPING_METHODS_VALUES,
  VENDOR_FEE_TYPES_VALUES
} from 'features/ContractDetails/constants'
import { useContractDetailsPopup } from 'features/ContractDetails/Providers/ContractDetailsPopupProvider'
import { getUser } from 'redux/store/user/getters'
import useRouter from 'hooks/useRouter'
import { canSeeMyContracts } from 'helper/common'
import { ContractCreationWays } from 'features/ContractDetails/types'

const useContractDetailsForm = () => {
  const router = useRouter()
  const user = useSelector(getUser)

  const contractDetailsContext = useContractDetailsContext()
  const { assignResponsiblePopup } = useContractDetailsPopup()

  const { details, isCreation, isFilesUpdate, paymentFrequencyOptions } =
    contractDetailsContext.state
  const {
    createContractAsync,
    updateContractDetailsAsync,
    setIsFilesUpdate,
    getCreationDataOptions
  } = contractDetailsContext.actions

  const [isChanged, setIsChanged] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const [form] = Form.useForm<IContractDetailsForm>()

  const _startDate = Form.useWatch('start_date', form)
  const _finishDate = Form.useWatch('finish_date', form)
  const vendor_fee_type = Form.useWatch('vendor_fee_type', form)
  const shipping_method = Form.useWatch('shipping_method', form)

  const [hasFormChanges, _setHasFormChanges] = useState(false)

  const { canEditStartDate, canEditFinishDate, isFormRequired } =
    useMemo(() => {
      const canEditDateByStatus =
        isCreation ||
        details.status === CONTRACT_STATUS.CONTRACTING ||
        details.status === CONTRACT_STATUS.UPLOAD_IN_PROGRESS ||
        details.status === CONTRACT_STATUS.REDLINING ||
        details.status === CONTRACT_STATUS.OUT_FOR_SIGNATURE

      const isExecutedAuto =
        details.status === CONTRACT_STATUS.EXECUTED &&
        details.creation_way === ContractCreationWays.AUTO

      return {
        canEditStartDate:
          canEditDateByStatus || (isExecutedAuto && !details.start_date),
        canEditFinishDate:
          canEditDateByStatus || (isExecutedAuto && !details.finish_date),
        isFormRequired:
          !isCreation &&
          ((details.status === CONTRACT_STATUS.UPLOAD_IN_PROGRESS &&
            details.creation_way === ContractCreationWays.MANUAL) ||
            isExecutedAuto)
      }
    }, [
      details.status,
      isCreation,
      details.creation_way,
      details.start_date,
      details.finish_date
    ])

  const disabledDateSameOrBeforeStartDate = useMemo(
    () => disabledDateSameOrBefore(_startDate),
    [_startDate]
  )

  useEffect(() => {
    if (vendor_fee_type) {
      getCreationDataOptions(vendor_fee_type)
      if (vendor_fee_type !== VENDOR_FEE_TYPES_VALUES.ADMIN) {
        form.resetFields(['admin_fee'])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendor_fee_type])

  useEffect(() => {
    if (isCreation && !vendor_fee_type)
      form.setFieldsValue({
        vendor_fee_type: VENDOR_FEE_TYPES_VALUES.NO_FEE
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreation, vendor_fee_type])

  useEffect(() => {
    if (isCreation && !shipping_method)
      form.setFieldsValue({
        shipping_method: SHIPPING_METHODS_VALUES.NOT_APPLICABLE
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreation, shipping_method])

  useEffect(() => {
    if (
      vendor_fee_type === VENDOR_FEE_TYPES_VALUES.MARKETPLACE_AR &&
      paymentFrequencyOptions.find((option) => option.value === 'quarterly')
    ) {
      form.setFieldsValue({
        payment_frequency: 'quarterly'
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentFrequencyOptions])

  const disabledDateAfterFinishDate = useMemo(
    () => disabledDateFinishDt(_finishDate),
    [_finishDate]
  )

  const finishDateFieldStatus = disabledDateSameOrBeforeStartDate(_finishDate)
    ? ('error' as const)
    : ('' as const)

  const triggerHasFormChanges = useCallback(() => {
    if (!hasFormChanges) {
      _setHasFormChanges(true)
    }
  }, [hasFormChanges])

  const handleFinishForm = useCallback(
    async (values: IContractDetailsForm) => {
      setIsChanged(false)
      setIsActive(false)
      if (finishDateFieldStatus === 'error') {
        return
      }
      if (isCreation) {
        const creationDetails = convertFormToManualCreation(values)
        if (user.role === ROLES.COMMUNITY_PRESIDENT) {
          assignResponsiblePopup.actions.open({ creationDetails })
          return
        }

        const contractUuid = await createContractAsync(creationDetails)

        if (contractUuid) {
          notification.success({
            message: `Contract ${VALIDATION_MESSAGES.SM0009} created`
          })
          router.push(
            `${routes.contracts}${
              canSeeMyContracts(user.role) ? '/my_contracts' : ''
            }/${contractUuid}`
          )
        }
        return
      }
      await updateContractDetailsAsync(convertFormToDetails(values))
      _setHasFormChanges(false)
    },
    [
      finishDateFieldStatus,
      isCreation,
      updateContractDetailsAsync,
      user.role,
      createContractAsync,
      assignResponsiblePopup.actions,
      router
    ]
  )

  const handleFinishFailedForm = useCallback(({ values }) => {
    const isCCNotSelected = !values.organization_contract_category
    if (isCCNotSelected) {
      notification.error({
        message: 'Please add contract category to create the contract.'
      })
    }
  }, [])

  const handleCancelForm = useCallback(() => {
    setIsActive(false)
    form.setFieldsValue(convertDetailsToForm(details))
    _setHasFormChanges(false)
    history.back()
  }, [details, form, setIsActive])

  const handleChangeForm = useCallback(() => {
    setIsActive(true)
    triggerHasFormChanges()
  }, [triggerHasFormChanges, setIsActive])

  useEffect(() => {
    if (!isFilesUpdate) {
      form.setFieldsValue(convertDetailsToForm(details))
    } else {
      setIsFilesUpdate(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details, form])

  useEffect(() => {
    if (details.status === CONTRACT_STATUS.OUT_FOR_SIGNATURE) {
      void form.validateFields()
    }
  }, [details.status, form])

  return {
    form,
    isCreation,
    isFormRequired,
    hasFormChanges,
    handleFinishForm,
    handleCancelForm,
    handleChangeForm,
    finishDateFieldStatus,
    disabledDateSameOrBeforeStartDate,
    disabledDateAfterFinishDate,
    setIsChanged,
    isChanged,
    setIsActive,
    isActive,
    details,
    handleFinishFailedForm,
    canEditStartDate,
    canEditFinishDate
  }
}

export default useContractDetailsForm
